import axios from 'axios';
import { getTenantBaseUrl } from '@/utils/tenant';

/**
 * Client portal API (/api/portal/*), for guests the firm shares matter files
 * with (OL-192).
 *
 * Its own axios instance, never the shared apiService. That one treats a 401
 * as an expired staff session: it clears the staff token, sends the browser to
 * the staff login, and swallows the rejection. Here a 401 means the guest's
 * session ended, which the portal handles by itself (see onSessionEnded).
 *
 * The session token travels in X-Portal-Session, not Authorization, so it can
 * never be mistaken for a staff token on either side.
 */
const portalClient = axios.create({
  baseURL: getTenantBaseUrl(),
});

const SESSION_KEY = 'oak_portal_session';

export const getPortalSession = () => {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
};

export const setPortalSession = (token) => {
  try {
    localStorage.setItem(SESSION_KEY, token);
  } catch {
    /* storage blocked: the session lasts as long as this page */
  }
};

export const clearPortalSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* nothing stored */
  }
};

let sessionEndedHandler = null;

/** The portal's provider registers what to do when the server ends the session. */
export const onSessionEnded = (handler) => {
  sessionEndedHandler = handler;
};

portalClient.interceptors.request.use((config) => {
  const token = getPortalSession();
  if (token) config.headers['X-Portal-Session'] = token;
  return config;
});

portalClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const sentSession = Boolean(error?.config?.headers?.['X-Portal-Session']);
    if (error?.response?.status === 401 && sentSession) {
      clearPortalSession();
      // Checking a stored session on page load is quiet: an old token there
      // is not news, and the guest may be on the set-password page.
      if (!error.config.quietSessionCheck) sessionEndedHandler?.();
    }
    throw await withReadableBlobError(error);
  },
);

/**
 * Under responseType blob, an error body arrives as a Blob, which hides
 * response.data.message from every caller. Re-inflate it to JSON.
 */
const withReadableBlobError = async (error) => {
  const body = error?.response?.data;
  if (body instanceof Blob) {
    try {
      error.response.data = JSON.parse(await body.text());
    } catch {
      /* not JSON — leave the blob in place */
    }
  }
  return error;
};

const data = (response) => response.data.data;

const portalService = {
  /** Firm name, logo and whether to show the greyed OAK features. No session needed. */
  getBranding: () => portalClient.get('/portal/branding').then(data),

  async login(email, password) {
    const session = await portalClient.post('/portal/login', { email, password }).then(data);
    setPortalSession(session.session_token);
    return session;
  },

  forgotPassword: (email) => portalClient.post('/portal/forgot-password', { email }).then((r) => r.data.message),

  /** Who an invite or reset link is for. */
  checkPasswordToken: (token) => portalClient.get(`/portal/password-token/${token}`).then(data),

  async setPassword(token, password, passwordConfirmation) {
    const session = await portalClient
      .post(`/portal/password-token/${token}`, { password, password_confirmation: passwordConfirmation })
      .then(data);
    setPortalSession(session.session_token);
    return session;
  },

  getMe: ({ quiet = false } = {}) => portalClient.get('/portal/me', { quietSessionCheck: quiet }).then(data),

  changePassword: (currentPassword, password, passwordConfirmation) =>
    portalClient.patch('/portal/me/password', {
      current_password: currentPassword,
      password,
      password_confirmation: passwordConfirmation,
    }),

  async logout() {
    try {
      await portalClient.post('/portal/logout');
    } finally {
      clearPortalSession();
    }
  },

  /** Bytes that come through our API, which needs the session header. */
  getBlob: (path) => portalClient.get(path, { responseType: 'blob' }).then((r) => r.data),

  /* ------------------------- matters (OL-195) ------------------------- */

  getMatters: () => portalClient.get('/portal/matters').then(data),

  /** { matter: details, nodes: the guest's tree } */
  getMatter: (matterId) => portalClient.get(`/portal/matters/${matterId}`).then(data),

  getPreview: (matterId, fileId) => portalClient.get(`/portal/matters/${matterId}/files/${fileId}/preview`).then(data),

  getDownloadUrl: (matterId, fileId) =>
    portalClient.get(`/portal/matters/${matterId}/files/${fileId}/download`).then((r) => r.data.data.url),

  /** A PDF's bytes for the View Only viewer; B2 sends no CORS headers. */
  getPdf: (matterId, fileId) => portalService.getBlob(`/portal/matters/${matterId}/files/${fileId}/pdf`),

  downloadFolderZip: (matterId, folderId) =>
    portalService.getBlob(`/portal/matters/${matterId}/folders/${folderId}/download`),

  client: portalClient,
};

export default portalService;
